import AddRoundedIcon from '@mui/icons-material/AddRounded';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useGlobalStore } from '@old/global-store/use-store';
import { updateSilobagTagsMassivelyService } from '@siloreal:shared:api/service/silobag/silobag-tag-massive-update';
import {
  listSilobagTagsService,
  type SilobagTagListEntry,
} from '@siloreal:shared:api/service/silobag/silobag-tag-list';
import * as UI from '@siloreal:shared:ui';
import * as HOOKS from '@siloreal:shared:hooks';

const SILOBAG_LIST_QUERY_KEY = ['silopro-vrr-list'] as const;
const EMPTY_ORGANIZATION_TAGS: SilobagTagListEntry[] = [];

const ACTION_SURFACE = {
  add: { border: '#6BAA00', bg: '#e1eecc', status: '#5a9400' },
  remove: { border: '#ea1818', bg: '#fbd1d1', status: '#c41414' },
} as const;
const NEUTRAL_SURFACE = { border: '#D1D5DB', bg: '#F8FAFC', status: '#9CA3AF' } as const;
const CLOSE_BUTTON_SX = { color: '#374151', fontWeight: 600, textTransform: 'uppercase' } as const;
const CONFIRM_BUTTON_SX = {
  bgcolor: '#6BAA00',
  color: '#fff',
  fontWeight: 600,
  textTransform: 'uppercase',
  borderRadius: '20px',
  px: 3,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#5a9300', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#6BAA0066', color: '#fff' },
} as const;

type TagAction = 'add' | 'remove';
type TagActions = Partial<Record<number, TagAction>>;

type Props = {
  open: boolean;
  silobagIds: readonly string[];
  onClose: () => void;
};

type TagActionRowProps = {
  entry: SilobagTagListEntry;
  action?: TagAction;
  languageId: string;
  onChange: (tagId: number, action: TagAction | null) => void;
};

function normalizeIds(ids: readonly string[]): string[] {
  return [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
}

function getErrorMessage(error: unknown): string {
  const i18n = HOOKS.useLocale.getState();
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const message = (responseData as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }

  if (error instanceof Error && error.message.trim()) return error.message;
  return i18n.t('temp.common.error.failed_to_update_tags');
}

function TagActionRow({ entry, action, languageId, onChange }: TagActionRowProps) {
  const i18n = HOOKS.useLocale();
  const { tag } = entry;
  const translation = tag.translations.find((item) => item.language === languageId);
  const label = translation?.value || tag.label;
  const isAdding = action === 'add';
  const isRemoving = action === 'remove';
  const surface = action ? ACTION_SURFACE[action] : NEUTRAL_SURFACE;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth: '100%',
      }}
    >
      <Tooltip title={i18n.t('temp.common.will_be_included')} arrow>
        <IconButton
          size="small"
          aria-label={`${i18n.t('temp.common.include')} ${label}`}
          aria-pressed={isAdding}
          onClick={() => onChange(tag.id, isAdding ? null : 'add')}
          sx={{
            width: 32,
            height: 32,
            color: isAdding ? ACTION_SURFACE.add.status : NEUTRAL_SURFACE.status,
            bgcolor: isAdding ? ACTION_SURFACE.add.bg : NEUTRAL_SURFACE.bg,
            border: '1px solid',
            borderColor: isAdding ? ACTION_SURFACE.add.border : NEUTRAL_SURFACE.border,
            borderRightWidth: isRemoving ? 1 : 0,
            borderRadius: '8px 0 0 8px',
            transition: 'color 150ms ease, border-color 150ms ease, background-color 150ms ease',
            '&:hover': {
              bgcolor: isAdding ? '#d4e8b4' : '#EEF0F2',
            },
          }}
        >
          <AddRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={translation?.tooltip || ''}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            minWidth: 0,
            minHeight: 32,
            p: '3px',
            bgcolor: surface.bg,
            border: '1px solid',
            borderColor: surface.border,
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderRadius: 0,
            transition: 'border-color 150ms ease, background-color 150ms ease',
          }}
        >
          <Chip
            icon={
              tag.image_url ? (
                <Box
                  component="img"
                  src={tag.image_url}
                  alt=""
                  sx={{ width: 14, height: 14, ml: 0.5, objectFit: 'contain' }}
                />
              ) : undefined
            }
            label={label}
            size="small"
            sx={{
              maxWidth: '100%',
              height: 24,
              bgcolor: tag.bg_color,
              color: tag.text_color,
              fontWeight: 600,
              borderRadius: '6px',
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        </Box>
      </Tooltip>

      <Tooltip title={i18n.t('temp.common.will_be_excluded')} arrow>
        <IconButton
          size="small"
          aria-label={`${i18n.t('temp.common.exclude')} ${label}`}
          aria-pressed={isRemoving}
          onClick={() => onChange(tag.id, isRemoving ? null : 'remove')}
          sx={{
            width: 32,
            height: 32,
            color: isRemoving ? ACTION_SURFACE.remove.status : NEUTRAL_SURFACE.status,
            bgcolor: isRemoving ? ACTION_SURFACE.remove.bg : NEUTRAL_SURFACE.bg,
            border: '1px solid',
            borderColor: isRemoving ? ACTION_SURFACE.remove.border : NEUTRAL_SURFACE.border,
            borderLeftWidth: isAdding ? 1 : 0,
            borderRadius: '0 8px 8px 0',
            transition: 'color 150ms ease, border-color 150ms ease, background-color 150ms ease',
            '&:hover': {
              bgcolor: isRemoving ? '#f8bcbc' : '#EEF0F2',
            },
          }}
        >
          <RemoveRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function SilobagTaggingModal({ open, silobagIds, onClose }: Props) {
  const queryClient = useQueryClient();
  const languageId = useGlobalStore((state) => state.preferedLanguageId);
  const selectedOrganizationId = useGlobalStore((state) => state.organizationId);
  const userOrganizationId = useGlobalStore((state) => state.userOrganizationId);
  const [tagActions, setTagActions] = useState<TagActions>({});
  const normalizedIds = useMemo(() => normalizeIds(silobagIds), [silobagIds]);
  const firstSilobagId = normalizedIds[0];
  const isOwnOrganization =
    selectedOrganizationId !== undefined && selectedOrganizationId === userOrganizationId;
  const tagsQuery = useQuery({
    queryKey: ['silobag-tagging-options', firstSilobagId],
    queryFn: async () => {
      // TODO: revisar si esto se puede incluir en el servicio ya existente para que el uso del servicio sea limpio y no requiera lógica extra
      const result = await listSilobagTagsService({ query: firstSilobagId });
      if (result.error) throw new Error(i18n.t('temp.common.error.failed_to_obtain_organization_tags'));
      return result;
    },
    enabled: open && firstSilobagId !== undefined,
  });
  const updateMutation = useMutation({ mutationFn: updateSilobagTagsMassivelyService });
  const organizationTags =
    tagsQuery.data?.response.find((scope) => scope.scope === 'organization')?.tags ?? EMPTY_ORGANIZATION_TAGS;
  const hasChanges = Object.keys(tagActions).length > 0;
  const isBusy = updateMutation.isPending;
  const isLoading = tagsQuery.isLoading || tagsQuery.isFetching;
  const i18n = HOOKS.useLocale();

  const handleClose = (): void => {
    if (!isBusy) onClose();
  };

  const handleTagActionChange = (tagId: number, action: TagAction | null): void => {
    setTagActions((currentActions) => {
      const nextActions = { ...currentActions };

      if (action === null) {
        delete nextActions[tagId];
      } else {
        nextActions[tagId] = action;
      }

      return nextActions;
    });
  };

  const handleConfirm = async (): Promise<void> => {
    if (!isOwnOrganization) {
      toast.error(i18n.t('temp.common.toasts.error.select_organization_before_apply'));
      return;
    }

    const addTagIds: number[] = [];
    const removeTagIds: number[] = [];

    Object.entries(tagActions).forEach(([tagId, action]) => {
      const numericTagId = Number(tagId);
      if (action === 'add') addTagIds.push(numericTagId);
      if (action === 'remove') removeTagIds.push(numericTagId);
    });

    if (normalizedIds.length === 0 || (addTagIds.length === 0 && removeTagIds.length === 0)) return;

    try {
      const result = await updateMutation.mutateAsync({
        ...(removeTagIds.length > 0
          ? { removeTags: { silobagIds: normalizedIds, tagIds: removeTagIds } }
          : {}),
        ...(addTagIds.length > 0 ? { addTags: { silobagIds: normalizedIds, tagIds: addTagIds } } : {}),
      });

      if (result.error || !result.response) throw new Error(result.message);

      toast.success(
        normalizedIds.length === 1
          ? i18n.t('temp.common.toasts.success.silobag_tags_updated')
          : `${i18n.t('temp.common.toasts.success.silobags_tags_updated_1')} ${normalizedIds.length} ${i18n.t('temp.common.toasts.success.silobags_tags_updated_2')}`
      );
      onClose();
      void queryClient.invalidateQueries({ queryKey: SILOBAG_LIST_QUERY_KEY });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const actions = (
    <>
      <Button variant="text" disabled={isBusy} onClick={handleClose} sx={CLOSE_BUTTON_SX}>
        {i18n.t('temp.common.close')}
      </Button>
      {tagsQuery.isError ? (
        <Button variant="contained" onClick={() => void tagsQuery.refetch()} sx={CONFIRM_BUTTON_SX}>
          {i18n.t('temp.common.retry')}
        </Button>
      ) : (
        <Button
          variant="contained"
          disabled={isBusy || !isOwnOrganization || !hasChanges || isLoading || organizationTags.length === 0}
          onClick={() => void handleConfirm()}
          sx={CONFIRM_BUTTON_SX}
        >
          {isBusy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : i18n.t('temp.common.confirm')}
        </Button>
      )}
    </>
  );

  return (
    <UI.Modal
      open={open}
      onClose={handleClose}
      title={i18n.t('temp.private.list.common.massive_tagging_modal.title')}
      description={`${i18n.t('temp.private.list.common.massive_tagging_modal.sub_title')} ${normalizedIds.length} ${
        normalizedIds.length === 1
          ? i18n.t('temp.private.list.common.selected_silobag')
          : i18n.t('temp.private.list.common.selected_silobags')
      }.`}
      actions={actions}
      size="md"
      fullWidth
      dividers
      disableBackdropClose={isBusy}
      tid="silobag-tagging-modal"
    >
      <Stack gap={2}>
        <Alert severity="info">{i18n.t('temp.private.list.common.massive_tagging_modal.informative')}</Alert>

        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" gap={1.5} py={5}>
            <CircularProgress size={30} />
            <UI.Typography variant="system">
              {i18n.t('temp.private.list.common.massive_tagging_modal.obtaining_tags')}...
            </UI.Typography>
          </Stack>
        ) : tagsQuery.isError ? (
          <Alert severity="error">
            {i18n.t('temp.private.list.common.massive_tagging_modal.alert_failed_to_obtain_tags')}
          </Alert>
        ) : organizationTags.length === 0 ? (
          <Alert severity="warning">
            {i18n.t('temp.private.list.common.massive_tagging_modal.alert_no_tags_in_organization')}
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, max-content)',
              justifyContent: 'space-evenly',
              justifyItems: 'start',
              rowGap: 1,
              maxHeight: 420,
              overflowY: 'auto',
              pr: 0.5,
            }}
          >
            {organizationTags.map((entry) => (
              <TagActionRow
                key={entry.tag.id}
                entry={entry}
                action={tagActions[entry.tag.id]}
                languageId={languageId}
                onChange={handleTagActionChange}
              />
            ))}
          </Box>
        )}

        {!isLoading && !tagsQuery.isError && organizationTags.length > 0 ? (
          <Stack direction="row" alignItems="flex-start" gap={0.75}>
            <HelpOutlineIcon sx={{ mt: '1px', flexShrink: 0, color: '#6BAA00', fontSize: 18 }} />
            <Typography variant="caption" color="#6BAA00" lineHeight={1.5}>
              <strong>{i18n.t('temp.private.list.common.massive_tagging_modal.question')}</strong>
              <br />
              {i18n.t('temp.private.list.common.massive_tagging_modal.contact')}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </UI.Modal>
  );
}
